/**
 * Field definitions for admin collections.
 * - Every select/combo field has predefined options.
 * - `default` values hydrate the form when creating a new item → creation never fails
 *   due to blank required fields.
 * - Free-text fields with common values use `suggestions` → rendered as combobox
 *   (select + free-type fallback) in CrudForm.
 */

// Canonical hardware categories (used by the Category select)
export const hwCategories = [
  'kioscos-autoservicio', 'pos', 'price-checkers', 'balanzas-comerciales',
  'pesaje-industrial', 'esl', 'carteleria-digital', 'aeropuertos',
  'airport-charging', 'impresion', 'scanners', 'perifericos', 'refurb',
  'servidores-lenovo', 'turnomaticos', 'software', 'vending', 'pda-handheld',
];

export const BRANDS = [
  'ALLCOM', 'CCL', 'ChiMEE', 'Cintaq', 'Datalogic', 'Fujitsu', 'HP', 'Honeywell',
  'iMin', 'Lenovo', 'Marques', 'NCR', 'Nebula', 'Nebullar', 'Pentagram',
  'Poslab', 'SOLUM', 'TCN', 'Toshiba', 'Wintec', 'Zebra',
];

export const SUBCATEGORIES = [
  'balanza-industrial', 'balanza-supermercado', 'compact', 'display-bar',
  'display-dual', 'display-mesa', 'display-standard', 'kiosco-autoservicio',
  'kiosco-turno', 'pantalla-llamado', 'pos-allinone', 'pos-android',
  'pos-robusto', 'professional', 'self-checkout', 'software-filas', 'special',
  'vending-bebidas', 'vending-combo', 'vending-frozen', 'vending-locker',
  'vending-specialty',
];

export const OFFER_TYPES = [
  'puntos-venta', 'balanzas', 'scanners', 'perifericos', 'autoservicio',
  'esl', 'vending', 'pda', 'servidores',
];

export const INDUSTRIES = [
  'supermercados', 'tiendas', 'farmacias', 'restaurantes',
  'industria-logistica', 'oficinas', 'banca', 'salud', 'aeropuertos',
  'sector-publico', 'mineria', 'vending', 'sda',
];

export const TIERS = ['entry', 'mid', 'premium', 'esencial', 'pro'];

export const productFields = [
  { key: 'name', label: 'Nombre del producto', type: 'text', required: true, default: '', hint: 'Ej: SOLUM Newton Pro' },
  { key: 'slug', label: 'Slug (URL única) · se autogenera si queda vacío', type: 'text', default: '', hint: 'Ej: solum-newton-pro' },
  { key: 'brand', label: 'Marca', type: 'combo', options: BRANDS, default: 'ALLCOM', required: true, hint: 'Selecciona una marca existente o escribe una nueva' },
  { key: 'category', label: 'Categoría principal (URL del detalle)', type: 'select', options: hwCategories, default: '', required: true, hint: 'Determina la URL del detalle: /hardware/{categoría}/{slug}' },
  { key: 'hw_slugs', label: 'Categorías Hardware adicionales (N:M)', type: 'hw_slugs', default: [], hint: 'Opcional — este producto aparecerá ADEMÁS en todas las categorías que selecciones (ej: una PDA puede vivir en POS + PDA/Handheld a la vez). Se cargan en vivo desde /admin/hw-categories.' },
  { key: 'category_display', label: 'Categoría visible (se autocompleta)', type: 'text', default: '', hint: 'Se genera automáticamente desde la categoría si queda vacío' },
  { key: 'subcategory', label: 'Subcategoría', type: 'combo', options: SUBCATEGORIES, default: '', hint: 'Selecciona o escribe una nueva' },
  { key: 'tier', label: 'Gama / Tier', type: 'select', options: TIERS, default: 'pro', required: true, hint: 'Esencial=económico, Pro=recomendado, Premium=top' },
  { key: 'solution_slugs', label: 'Soluciones asociadas (N:M)', type: 'solution_slugs', default: [], required: true, hint: 'Obligatorio — vincula este producto a al menos una solución. El backend sugerirá automáticamente el espejo de la Categoría.' },
  { key: 'industries', label: 'Industrias', type: 'multicheck', options: INDUSTRIES, default: [], hint: 'Marca todas las industrias aplicables' },
  { key: 'type', label: 'Tipo (para bloque de Ofertas)', type: 'combo', options: OFFER_TYPES, default: '', hint: 'Selecciona o escribe' },
  { key: 'summary', label: 'Resumen corto (1 línea)', type: 'text', default: '', hint: 'Descripción breve para cards' },
  { key: 'description', label: 'Descripción completa', type: 'textarea', default: '', hint: 'Descripción detallada del producto' },
  { key: 'price', label: 'Precio CLP', type: 'text', default: '', hint: 'Solo números (ej: 2490000) — se mostrará como $2.490.000' },
  { key: 'price_display', label: 'Precio visible web (opcional)', type: 'text', default: '', hint: 'Si se deja vacío, se autoformatea desde Precio CLP' },
  { key: 'liquidacion', label: 'En liquidación', type: 'select', options: ['false', 'true'], default: 'false', hint: 'true = producto está liquidándose (con o sin descuento)' },
  { key: 'show_in_offers', label: 'Mostrar en Ofertas Página de Inicio', type: 'select', options: ['false', 'true'], default: 'false', hint: 'true = aparece destacado en la sección "Equipos en oferta" del landing' },
  { key: 'price_original', label: 'Precio original (solo liquidación)', type: 'text', default: '', hint: 'Precio antes del descuento, ej: 3990000' },
  { key: 'images', label: 'Galería de fotos', type: 'images', default: [], hint: 'Sube o arrastra fotos. La primera es la principal.' },
  { key: 'spec_doc', label: 'Ficha técnica (PDF)', type: 'document', default: null, hint: 'Sube PDF con especificaciones — aparecerá como botón en el sitio público' },
  { key: 'brochure_doc', label: 'Brochure comercial (PDF)', type: 'document', default: null, hint: 'Sube PDF comercial' },
  { key: 'use_cases', label: 'Casos de uso', type: 'array', default: [], hint: 'Una por línea: Supermercado, Restaurant, Farmacia' },
  { key: 'features', label: 'Características clave', type: 'array', default: [], hint: 'Una por línea: Pantalla táctil, NFC, etc.' },
  { key: 'specs', label: 'Especificaciones técnicas', type: 'specs', default: {}, hint: 'Formato: Clave: Valor (una por línea)' },
];

export const solutionFields = [
  { key: 'title', label: 'Título', type: 'text', required: true, default: '' },
  { key: 'slug', label: 'Slug (URL)', type: 'text', required: true, default: '' },
  { key: 'badge', label: 'Badge (etiqueta hero)', type: 'text', default: '' },
  { key: 'icon', label: 'Ícono (lucide)', type: 'text', default: 'Zap' },
  { key: 'h1', label: 'H1 (titular hero)', type: 'text', default: '' },
  { key: 'subtitle', label: 'Subtítulo hero', type: 'textarea', default: '' },
  { key: 'description', label: 'Descripción', type: 'textarea', default: '' },
  { key: 'features', label: 'Características', type: 'array', default: [], hint: 'una por línea' },
  { key: 'products', label: 'Productos relacionados', type: 'array', default: [], hint: 'un nombre por línea' },
];

export const caseFields = [
  { key: 'title', label: 'Título del caso', type: 'text', required: true, default: '' },
  { key: 'slug', label: 'Slug (URL)', type: 'text', required: true, default: '' },
  { key: 'industry', label: 'Industria', type: 'select', options: ['Retail', 'Banca', 'Salud', 'Minería', 'Aeropuertos', 'Sector Público', 'Gastronomía'], default: 'Retail' },
  { key: 'summary', label: 'Resumen', type: 'textarea', default: '' },
  { key: 'problem', label: 'Problema / Desafío', type: 'textarea', default: '' },
  { key: 'solution', label: 'Solución ALLCOM', type: 'textarea', default: '' },
  { key: 'quote', label: 'Cita del cliente', type: 'textarea', default: '' },
  { key: 'metrics', label: 'Métricas', type: 'metrics', default: [], hint: 'Etiqueta: Valor, una por línea' },
  { key: 'products_used', label: 'Productos utilizados', type: 'array', default: [], hint: 'un nombre por línea' },
];

export const crewFields = [
  { key: 'name', label: 'Nombre completo', type: 'text', required: true, default: '' },
  { key: 'role', label: 'Cargo (ej: CEO & Fundador, CFO, CIO)', type: 'text', required: true, default: '' },
  { key: 'photo', label: 'URL de foto', type: 'text', default: '' },
  { key: 'bio', label: 'Reseña / Biografía', type: 'textarea', default: '' },
  { key: 'linkedin', label: 'LinkedIn URL', type: 'text', default: '' },
  { key: 'grayscale', label: 'Foto en escala de grises', type: 'select', options: ['false', 'true'], default: 'false' },
  { key: 'order', label: 'Orden (1, 2, 3...)', type: 'text', default: '99' },
];

export const hwCategoryFields = [
  { key: 'name', label: 'Nombre de la categoría', type: 'text', required: true, default: '' },
  { key: 'slug', label: 'Slug (URL, ej: kioscos-autoservicio)', type: 'combo', options: hwCategories, required: true, default: '' },
  { key: 'icon', label: 'Ícono (lucide, ej: monitor, scale, tag)', type: 'text', default: 'Package' },
  { key: 'description', label: 'Descripción', type: 'textarea', default: '' },
  { key: 'product_count', label: 'Cantidad de productos (referencial)', type: 'text', default: '0' },
  { key: 'order', label: 'Orden (1, 2, 3...)', type: 'text', default: '99' },
];

export const RELEASE_TAGS = [
  'Feature', 'Aeropuertos', 'Admin', 'SEO', 'Seguridad', 'UX',
  'Editorial', 'IA', 'AVX', 'Performance', 'Calculator', 'Catálogo',
  'Reliability', 'Fix',
];

export const RELEASE_ICONS = [
  'Sparkles', 'Rocket', 'Wrench', 'ShoppingBag', 'BarChart3', 'Bot',
  'ShieldCheck', 'Award', 'Zap', 'Globe', 'Newspaper', 'Plane',
];

export const RELEASE_COLORS = [
  '#0EA5E9', '#0A2540', '#0191B4', '#F8D90F', '#10B981',
  '#FE7A15', '#7C3AED', '#EC4899', '#EF4444', '#64748B',
];

export const releaseFields = [
  { key: 'version', label: 'Versión (ej: v3.5)', type: 'text', required: true, default: 'v', hint: 'Identificador único, ej. v3.5' },
  { key: 'date', label: 'Fecha legible (ej: Mayo 2026 · Q3 2026)', type: 'text', required: true, default: '', hint: 'Cómo aparece debajo de la versión' },
  { key: 'headline', label: 'Titular del release', type: 'text', required: true, default: '', hint: 'Una línea, ej: "Plataforma B2B profesional"' },
  { key: 'intro', label: 'Intro (2-3 líneas)', type: 'textarea', required: true, default: '', hint: 'Resumen humanizado del release' },
  { key: 'icon', label: 'Ícono lucide', type: 'combo', options: RELEASE_ICONS, default: 'Sparkles', hint: 'Aparece en el círculo de color de la card' },
  { key: 'color', label: 'Color del dot/icono', type: 'combo', options: RELEASE_COLORS, default: '#0EA5E9', hint: 'Hex con #' },
  { key: 'items', label: 'Items (uno por línea, formato "Tag: descripción")', type: 'release_items', default: [], hint: 'Tags válidos: ' + RELEASE_TAGS.join(', ') },
  { key: 'display_order', label: 'Orden (menor = aparece primero)', type: 'text', default: '0' },
  { key: 'published', label: '¿Publicado?', type: 'select', options: ['true', 'false'], default: 'true' },
];

/** Build initial form state from a list of field defs. */
export const buildDefaults = (fields) => {
  const defaults = {};
  (fields || []).forEach((f) => {
    if (f.default !== undefined) defaults[f.key] = Array.isArray(f.default) ? [...f.default] : (typeof f.default === 'object' && f.default !== null ? { ...f.default } : f.default);
  });
  return defaults;
};
